import { useSearchParams } from 'react-router-dom';
import type { Capability } from '../api/capabilities';
import { useCapabilities } from '../Capabilities';
import { WorkPage } from './WorkPage';
import { WorkloadPage } from './WorkloadPage';
import { PeoplePage } from './PeoplePage';
import { ObservationsPanel } from '../components/pulse/ObservationsPanel';
import { CollectorsPanel } from '../components/pulse/CollectorsPanel';
import { MetricsPanel } from '../components/pulse/MetricsPanel';
import './TeamPulsePage.css';

/**
 * Team Pulse — one page, six tabs, conclusions first.
 *
 * **Restored 2026-09-27, reported as "I don't see the team pulse tab
 * pages any more, which is the most I use".** The predecessor had this as
 * a single nav item with six tabs. The port split three of them into
 * separate top-level pages — Work, Workload, People — and dropped the
 * other three, and I recorded that as "Team Pulse → Work + Workload +
 * People, ported". It was three tabs of six, and the grouping itself was
 * the thing being used.
 *
 * **The tab order is the predecessor's and it is an argument, not a
 * layout.** Its comment is worth keeping verbatim: tabs run
 * conclusions-first, drilling down to raw data — the reverse of the data
 * flow. The reader starts with synthesised insight and only descends when
 * the answer does not explain itself:
 *
 *     Insights (pipeline + findings + narrative)
 *       └─ Workload (everyone now, one grid)
 *           └─ People (one person's series)
 *               └─ Observations (raw tracked facts)
 *                   └─ Metrics (catalog + time series)
 *                       └─ Collectors (raw source state)
 *
 * The pipeline still flows collectors → … → insights; the UI is the
 * reverse so you start at the answer.
 *
 * **The tab lives in the URL, which the predecessor's did not.** It held
 * the tab in `useState`, so a reload dropped you back to Insights and a
 * tab could not be linked or bookmarked. `?tab=observations` is the whole
 * change and it is why the old `/work`, `/workload` and `/people` routes
 * can redirect here instead of breaking.
 */

type TabId =
  | 'insights'
  | 'workload'
  | 'people'
  | 'observations'
  | 'metrics'
  | 'collectors';

/**
 * `requires` is the extension that serves the tab's data, as
 * `ROUTE_DEPENDENCIES` records it. The page itself needs `work.trends` and
 * is gated on it in the router; a tab's own need is checked here, and an
 * unavailable tab is hidden — as the sidebar hides a page — rather than
 * shown to answer 503.
 */
const TABS: Array<{ id: TabId; label: string; hint: string; requires?: Capability }> = [
  { id: 'insights', label: 'Insights', hint: 'pipeline, findings, narrative' },
  { id: 'workload', label: 'Workload', hint: 'everyone now, one grid', requires: 'work.metrics' },
  { id: 'people', label: 'People', hint: 'one person, over time', requires: 'work.metrics' },
  { id: 'observations', label: 'Observations', hint: 'raw tracked facts', requires: 'work.metrics' },
  { id: 'metrics', label: 'Metrics', hint: 'catalog and series', requires: 'work.metrics' },
  { id: 'collectors', label: 'Collectors', hint: 'raw source state', requires: 'work.collect' },
];

export function TeamPulsePage() {
  const [params, setParams] = useSearchParams();
  const caps = useCapabilities();
  const tabs = TABS.filter((t) => !t.requires || caps?.[t.requires] === true);
  const raw = params.get('tab') ?? '';
  // An unknown or unavailable tab falls back to the first rather than
  // rendering nothing, so a stale bookmark lands somewhere useful.
  const active: TabId = tabs.some((t) => t.id === raw) ? (raw as TabId) : 'insights';

  const select = (id: TabId) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    // `replace`, so six tab clicks do not put six entries in history and
    // make Back mean "previous tab" instead of "previous page".
    setParams(next, { replace: true });
  };

  return (
    <div className="pulse-page">
      <header className="pulse-page__header">
        <h1 className="pulse-page__title">Team Pulse</h1>
        <p className="pulse-page__note">
          Conclusions first: insights → workload → people → observations →
          metrics → collectors. The pipeline runs the other way.
        </p>
      </header>

      <nav className="pulse-tabs" aria-label="Team Pulse sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pulse-tab${active === t.id ? ' is-active' : ''}`}
            onClick={() => select(t.id)}
            title={t.hint}
            aria-current={active === t.id ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="pulse-panel">
        {active === 'insights' && <WorkPage />}
        {active === 'workload' && <WorkloadPage />}
        {active === 'people' && <PeoplePage />}
        {active === 'observations' && <ObservationsPanel />}
        {active === 'metrics' && <MetricsPanel />}
        {active === 'collectors' && <CollectorsPanel />}
      </div>
    </div>
  );
}
